/* Copiar con formato: lo elegido de una planilla, para un correo o para
 * WhatsApp (ticket 464).
 *
 * Lautaro (20/10/2026): «necesito que en mis planillas cuando yo copie y
 * pegue algo para nose, pasarselo a alguien por whatsapp o pegarlo en correo
 * me quede en un formato asi», con una captura de un bitren de su ONLY
 * BITRENES (hoja ARRIBOS BITREN) como se ve en la grilla, y «NO asi: 19  1564
 * 1049 LA RUBIA ROLANDO R.VICTORIA EN AZUL RGD   RGD», que es el texto con
 * tabuladores que pegaba hasta ahora.
 *
 * ── LO QUE VA AL PORTAPAPELES ───────────────────────────────────────────────
 *
 * Al copiar (Ctrl+C o «Copiar» de los menús) van dos cosas, y cada programa
 * toma la que entiende:
 *   · `text/plain`, el de siempre (`aTSV` en MiEspacio): tabuladores y saltos.
 *     Lo usa la misma planilla al pegar, y lo que no entiende tablas.
 *   · `text/html`, una `<table>` con estilos en línea (`htmlDeSeleccion`): un
 *     correo (GroupWise, Gmail, Outlook) la pega como tabla, con sus bordes, y
 *     Excel y Sheets también la prefieren: pegan con el formato. Una celda
 *     sola, o una combinada sola, no lleva tabla: pegada en un correo o en un
 *     formulario es un dato, no un cuadrito con borde. Más de
 *     `TOPE_CELDAS_TABLA` celdas tampoco: se arma dentro del evento de copiar
 *     y trabaría la pantalla, y nadie pega eso en un correo (`tablaParaCopiar`).
 * Lo que se copia es lo elegido, ni más ni menos: lo que se ve marcado. Que un
 * bitren se elija entero arrastrando lo hace la selección misma
 * (`rangoElegido` de `shared/planillaCombinadas`), no el copiar.
 * Para WhatsApp, que no muestra tablas, «Copiar como imagen» arma un PNG de lo
 * mismo (`dibujarSeleccion`) y lo pone en el portapapeles (`copiarImagen`);
 * WhatsApp Web y el de escritorio lo pegan como foto.
 *
 * ── CÓMO SE VE ──────────────────────────────────────────────────────────────
 *
 *   · Las combinadas, como UNA celda (`rowspan`/`colspan`): el 1564 arriba del
 *     1565 y el resto del bitren a la altura de los dos. Antes, arrastrando
 *     hasta la esquina de abajo de un bitren, la grilla tomaba el tramo de
 *     abajo como su ancla y lo elegido quedaba sin el renglón del 1565 (por
 *     eso su «NO asi» no lo tiene); ahora la selección crece hasta abarcar
 *     las combinadas que toca, como en Excel. Una combinada que lo elegido
 *     corta igual (con el teclado, o una columna entera que cruza un título),
 *     sale cortada, como se ve.
 *   · Sin las columnas y filas vacías del final (`sinVaciasAlFinal`): una fila
 *     entera llega hasta la Z.
 *   · Lo que la persona le puso a la celda: fondo, color de letra, negrita,
 *     itálica, subrayado, tachado, tamaño, alineación y bordes; las franjas de
 *     colores alternos y el formato condicional (`formatoVisto`, el mismo de
 *     imprimir). Sin alineación, como la grilla: los números a la derecha y lo
 *     demás a la izquierda; una combinada de varias filas, arriba.
 *   · Los COLORES DEL TEMA NO: el azul marino de la captura es el tema oscuro
 *     del hub, no de la planilla. En un correo de fondo blanco un bloque azul
 *     pesa y no se lee como el resto del mail, así que la tabla va como la
 *     grilla en el tema claro: sin fondo, letra oscura (`TEXTO`) y líneas
 *     finas grises (`LINEA`). Un fondo puesto a mano sí viaja, con la letra
 *     blanca si es oscuro, como en la grilla. La imagen, igual, sobre blanco.
 *   · Sin letras de columna ni números de fila, y sin agregar los títulos de
 *     las columnas: va lo elegido, nada más. Excel y Sheets pegan esta tabla,
 *     así que lo que se agregara acá aparecería también al pegar en Excel. El
 *     que quiere los títulos, los elige.
 *   · La letra no se fija en la tabla: en el correo toma la del mail (y en
 *     Excel, la de la hoja). En la imagen, Arial de 13 px como la grilla.
 *
 * ── LO QUE VIENE DE LA BASE ─────────────────────────────────────────────────
 *
 * Como al imprimir: colores, tamaños, anchos y altos salen de la planilla
 * guardada, que se puede escribir por fuera de la pantalla. En un `style` sólo
 * entra lo que se entiende (`colorOk`, `medidaOk`, las alineaciones) y el
 * texto va escapado: un valor raro no sale, en vez de colarse como HTML.
 */

import { refDe } from "./planilla";
import { esUnDato, mapaCombinadas } from "./planillaCombinadas";
import {
  ALINEACIONES, areaUsada, colorOk, formatoVisto, medidaOk, TOPE_LETRA, VERTICALES,
} from "./planillaImprimir";
import { esOscuro, formatear } from "./planillaOps";

/* Los colores de la tabla: las líneas finas de cada celda, la letra de una
   celda sin color propio y los bordes puestos con Formato › Bordes. */
export const LINEA = "#a9b2bc";
export const TEXTO = "#17212b";
export const BORDE_MARCADO = "#111111";

/* Lo que se le dice a la persona después de «Copiar como imagen». */
export const IMAGEN_COPIADA = "Imagen copiada. Andá al chat de WhatsApp y pegala con Ctrl+V.";
export const IMAGEN_NO_SE_COPIA = "Este navegador no deja copiar imágenes. Bajala y mandala por WhatsApp como foto.";
export const IMAGEN_MUY_GRANDE = "Es mucho para una imagen: elegí menos filas o columnas.";
export const IMAGEN_FALLO = "No se pudo armar la imagen. Probá de nuevo.";
export const IMAGEN_SIN_LIENZO = "Este navegador no puede armar imágenes. Probá desde Chrome o Edge.";

/* Hasta cuántas celdas lleva tabla (`text/html`) lo que se copia: 200 filas
   de 25 columnas. Más que eso va el texto solo, como antes (ver arriba). */
export const TOPE_CELDAS_TABLA = 5000;

// Como la grilla: columnas de 96 px, filas de 30, letra de 13.
const ANCHO_COL = 96;
const ALTO_FILA = 30;
const LETRA = 13;

const escapar = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const renglones = (texto) => String(texto).split(/\r\n|\r|\n/);

/* ── Lo que se copia ─────────────────────────────────────────────────────── */

/* El estilo de una celda, ya entendido: sólo valores que se pueden escribir
   en un `style` o en el lienzo. */
function estiloDe(fmt, esNumero, enBloque) {
  const f = fmt || {};
  const bg = colorOk(f.bg);
  const b = f.bordes || {};
  return {
    bg,
    color: colorOk(f.color) || (bg && esOscuro(bg) ? "#ffffff" : TEXTO),
    bold: !!f.bold,
    italic: !!f.italic,
    subrayado: !!f.subrayado,
    tachado: !!f.tachado,
    size: medidaOk(f.size, TOPE_LETRA),
    align: ALINEACIONES.has(f.align) ? f.align : (esNumero ? "right" : "left"),
    valign: VERTICALES.has(f.valign) ? f.valign : (enBloque ? "top" : "middle"),
    bordes: { t: !!b.t, r: !!b.r, b: !!b.b, l: !!b.l },
  };
}

/* Lo elegido, listo para la tabla y para la imagen. `rg` es el rango elegido
   (ya dentro de la hoja); `valores`, la hoja
   calculada; `estilos`, el Map ref → estilo del formato condicional.
   Devuelve `{ filas, anchos, altos }`: por cada fila de lo elegido, sus
   celdas `{ fila, col, alto, largo, texto, estilo }` (las tapadas por una
   combinada no están), y el ancho de cada columna y el alto de cada fila como
   en la grilla. Una combinada cortada por el rango se corta igual; si su
   ancla quedó afuera, va sin el valor, como el tramo de abajo en la grilla y
   como en el `text/plain`. */
export function seleccionConFormato(hoja, valores, rg, { estilos = null } = {}) {
  const usada = areaUsada(hoja.celdas || []);
  const mapa = mapaCombinadas(hoja.combinadas);
  const filas = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) {
    const fila = [];
    for (let c = rg.c0; c <= rg.c1; c += 1) {
      const m = mapa.get(`${r},${c}`);
      let [ar, ac, alto, largo, conValor] = [r, c, 1, 1, true];
      if (m) {
        const [f0, k0] = [Math.max(m.r0, rg.r0), Math.max(m.c0, rg.c0)];
        if (r !== f0 || c !== k0) continue; // la tapa otra parte de la misma combinada
        alto = Math.min(m.r1, rg.r1) - f0 + 1;
        largo = Math.min(m.c1, rg.c1) - k0 + 1;
        [ar, ac] = [m.r0, m.c0];
        conValor = r === m.r0 && c === m.c0;
      }
      const fmt = formatoVisto(hoja, estilos, usada, ar, ac, r);
      const v = conValor && valores[ar] ? valores[ar][ac] : "";
      fila.push({
        fila: r - rg.r0, col: c - rg.c0, alto, largo,
        texto: formatear(v, fmt), estilo: estiloDe(fmt, typeof v === "number", alto > 1),
      });
    }
    filas.push(fila);
  }
  const anchos = [];
  for (let c = rg.c0; c <= rg.c1; c += 1) anchos.push(medidaOk(hoja.anchos && hoja.anchos[c]) || ANCHO_COL);
  const altos = [];
  for (let r = rg.r0; r <= rg.r1; r += 1) altos.push(medidaOk(hoja.altos && hoja.altos[r]) || ALTO_FILA);
  return { filas, anchos, altos };
}

/* Sin las columnas y las filas del final que no tienen nada (ni texto, ni
   fondo, ni bordes): eligiendo la fila entera (clic en su número) lo elegido
   llega hasta la Z, y la tabla del correo o la foto saldrían con una docena
   de columnas vacías (con la columna entera, lo mismo con las filas). Las de
   adelante y las del medio quedan: las eligió la persona. Siempre queda al
   menos una celda. */
function sinVaciasAlFinal(modelo) {
  let [filas, cols] = [1, 1];
  for (const fila of modelo.filas) {
    for (const cel of fila) {
      const e = cel.estilo;
      if (!cel.texto && !e.bg && !e.bordes.t && !e.bordes.r && !e.bordes.b && !e.bordes.l) continue;
      filas = Math.max(filas, cel.fila + cel.alto);
      cols = Math.max(cols, cel.col + cel.largo);
    }
  }
  return {
    filas: modelo.filas.slice(0, filas).map((fila) => fila.filter((cel) => cel.col < cols).map((cel) => ({
      ...cel, alto: Math.min(cel.alto, filas - cel.fila), largo: Math.min(cel.largo, cols - cel.col),
    }))),
    anchos: modelo.anchos.slice(0, cols),
    altos: modelo.altos.slice(0, filas),
  };
}

/* ── La tabla para el correo (text/html) ─────────────────────────────────── */

/* Como Sheets: arriba, que el texto viene en UTF-8 (sin eso, algún programa
   de Windows lee mal las tildes de «UBICACIÓN»). */
const META = '<meta http-equiv="content-type" content="text/html; charset=utf-8">';
const LADOS = [["t", "top"], ["r", "right"], ["b", "bottom"], ["l", "left"]];

function cssDe(e) {
  const s = [`border:1px solid ${LINEA}`, "padding:4px 8px"];
  for (const [k, lado] of LADOS) if (e.bordes[k]) s.push(`border-${lado}:2px solid ${BORDE_MARCADO}`);
  if (e.bg) s.push(`background:${e.bg}`);
  s.push(`color:${e.color}`);
  if (e.bold) s.push("font-weight:700");
  if (e.italic) s.push("font-style:italic");
  const deco = [e.subrayado && "underline", e.tachado && "line-through"].filter(Boolean).join(" ");
  if (deco) s.push(`text-decoration:${deco}`);
  if (e.size) s.push(`font-size:${e.size}px`);
  s.push(`text-align:${e.align}`, `vertical-align:${e.valign}`);
  return s.join(";");
}

/* La `<table>` de lo elegido, con los estilos en cada celda (los correos se
   comen las hojas de estilo). `border`, `cellspacing` y `cellpadding` van
   también como atributos, para el programa de correo que no lee el CSS. Sin
   las filas y columnas vacías del final (`sinVaciasAlFinal`). Una celda con
   un salto de renglón adentro (pegada de Excel) sale en dos renglones de la
   MISMA celda: el `mso-data-placement` es para Excel, que con un `<br>` solo
   la parte en dos filas. Los espacios seguidos (o al principio o al final)
   se juntarían en uno, como en cualquier HTML: esa celda lleva
   `white-space:pre-wrap`, sólo ella. */
const SALTO = '<br style="mso-data-placement:same-cell">';
const conEspacios = (lineas) => lineas.some((l) => / {2}|^ | $/.test(l));
export function htmlDeSeleccion(seleccion) {
  const modelo = sinVaciasAlFinal(seleccion);
  const filas = modelo.filas.map((fila) => {
    const tds = fila.map((cel) => {
      const span = (cel.alto > 1 ? ` rowspan="${cel.alto}"` : "") + (cel.largo > 1 ? ` colspan="${cel.largo}"` : "");
      const lineas = renglones(cel.texto);
      const css = cssDe(cel.estilo) + (conEspacios(lineas) ? ";white-space:pre-wrap" : "");
      return `<td${span} style="${escapar(css)}">${cel.texto === "" ? "" : lineas.map(escapar).join(SALTO)}</td>`;
    });
    return `<tr>${tds.join("")}</tr>`;
  });
  return `${META}<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;border:1px solid ${LINEA}">${filas.join("")}</table>`;
}

/* El `text/html` de copiar `rg` (ya dentro de la hoja), o null si va el
   texto solo: una celda sola o lo que cae dentro de una combinada (es un
   dato), o más de `TOPE_CELDAS_TABLA` celdas. */
export function tablaParaCopiar(hoja, valores, rg, opciones = {}) {
  if (esUnDato(rg, hoja.combinadas)) return null;
  if ((rg.r1 - rg.r0 + 1) * (rg.c1 - rg.c0 + 1) > TOPE_CELDAS_TABLA) return null;
  return htmlDeSeleccion(seleccionConFormato(hoja, valores, rg, opciones));
}

/* ── La imagen para WhatsApp ─────────────────────────────────────────────── */

const FUENTE = "Arial, Helvetica, sans-serif";
const AIRE_X = 7; // los márgenes de la celda en la grilla
const AIRE_Y = 5;
/* El lado más largo del lienzo, en px de la imagen. Chrome no dibuja lienzos
   de más de 16384 × 16384; con margen. Se dibuja al doble para que se lea
   nítida en el teléfono; si al doble no entra, en tamaño real; si tampoco, no
   se dibuja (`IMAGEN_MUY_GRANDE`). */
const LADO_MAXIMO = 16000;

const fuenteDe = (e) => `${e.italic ? "italic " : ""}${e.bold ? "700 " : ""}${e.size || LETRA}px ${FUENTE}`;
const altoDeRenglon = (e) => Math.round((e.size || LETRA) * 1.3);

/* Si el navegador puede dibujar (un `<canvas>` con contexto 2d). Sin eso no
   hay imagen, y el aviso es otro que el de «es mucho». */
export function hayLienzo(documento = typeof document === "undefined" ? null : document) {
  try {
    const lienzo = documento && documento.createElement ? documento.createElement("canvas") : null;
    return !!(lienzo && lienzo.getContext && lienzo.getContext("2d"));
  } catch { return false; }
}

/* El PNG de lo elegido en un `<canvas>`, o null si es mucho para una imagen
   (o el navegador no tiene lienzo: la pantalla lo mira antes, `hayLienzo`). Cada columna mide lo que en la grilla, o
   más si el texto de una celda de esa columna no entra: en la grilla se corta
   y se ve entero al elegirlo; en la foto, no habría cómo. Lo mismo con el alto
   de una fila de una celda con varios renglones. Las columnas y filas vacías
   del final no salen (`sinVaciasAlFinal`). */
export function dibujarSeleccion(seleccion, { documento = typeof document === "undefined" ? null : document, escala = 2 } = {}) {
  const lienzo = documento && documento.createElement ? documento.createElement("canvas") : null;
  const ctx = lienzo && lienzo.getContext ? lienzo.getContext("2d") : null;
  if (!ctx) return null;
  const modelo = sinVaciasAlFinal(seleccion);
  const anchos = modelo.anchos.slice();
  const altos = modelo.altos.slice();
  for (const fila of modelo.filas) {
    for (const cel of fila) {
      if (!cel.texto) continue;
      const lineas = renglones(cel.texto);
      ctx.font = fuenteDe(cel.estilo);
      if (cel.largo === 1) {
        const w = Math.max(...lineas.map((l) => ctx.measureText(l).width)) + 2 * AIRE_X + 1;
        anchos[cel.col] = Math.max(anchos[cel.col], Math.ceil(w));
      }
      if (cel.alto === 1) {
        const h = lineas.length * altoDeRenglon(cel.estilo) + 2 * AIRE_Y;
        altos[cel.fila] = Math.max(altos[cel.fila], Math.ceil(h));
      }
    }
  }
  const xs = [0];
  anchos.forEach((w, i) => xs.push(xs[i] + w));
  const ys = [0];
  altos.forEach((h, i) => ys.push(ys[i] + h));
  const [W, H] = [xs[xs.length - 1] + 1, ys[ys.length - 1] + 1];
  let k = escala;
  if (Math.max(W, H) * k > LADO_MAXIMO) k = 1;
  if (Math.max(W, H) * k > LADO_MAXIMO) return null;
  // Cambiar el tamaño del lienzo lo deja en blanco y sin la escala.
  lienzo.width = W * k;
  lienzo.height = H * k;
  ctx.setTransform(k, 0, 0, k, 0, 0);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  const celdas = [];
  for (const fila of modelo.filas) {
    for (const cel of fila) {
      const [x, y] = [xs[cel.col], ys[cel.fila]];
      celdas.push({ cel, x, y, w: xs[cel.col + cel.largo] - x, h: ys[cel.fila + cel.alto] - y });
    }
  }
  // Primero los fondos, después las líneas finas, el texto y arriba de todo
  // los bordes marcados: así el fondo de una celda no tapa la línea de otra.
  for (const { cel, x, y, w, h } of celdas) {
    if (!cel.estilo.bg) continue;
    ctx.fillStyle = cel.estilo.bg;
    ctx.fillRect(x, y, w, h);
  }
  ctx.strokeStyle = LINEA;
  ctx.lineWidth = 1;
  for (const { x, y, w, h } of celdas) ctx.strokeRect(x + 0.5, y + 0.5, w, h);
  for (const { cel, x, y, w, h } of celdas) {
    if (!cel.texto) continue;
    const e = cel.estilo;
    const lineas = renglones(cel.texto);
    const lh = altoDeRenglon(e);
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.font = fuenteDe(e);
    ctx.fillStyle = e.color;
    ctx.textBaseline = "middle";
    ctx.textAlign = e.align === "center" || e.align === "right" ? e.align : "left";
    const tx = e.align === "center" ? x + w / 2 : e.align === "right" ? x + w - AIRE_X : x + AIRE_X;
    const primera = e.valign === "top" ? y + AIRE_Y + lh / 2
      : e.valign === "bottom" ? y + h - AIRE_Y - lh * (lineas.length - 0.5)
        : y + h / 2 - (lh * (lineas.length - 1)) / 2;
    lineas.forEach((l, i) => {
      const ty = primera + i * lh;
      ctx.fillText(l, tx, ty);
      if (!e.subrayado && !e.tachado) return;
      const largo = ctx.measureText(l).width;
      const x0 = ctx.textAlign === "center" ? tx - largo / 2 : ctx.textAlign === "right" ? tx - largo : tx;
      ctx.strokeStyle = e.color;
      ctx.beginPath();
      if (e.subrayado) { ctx.moveTo(x0, ty + lh * 0.4); ctx.lineTo(x0 + largo, ty + lh * 0.4); }
      if (e.tachado) { ctx.moveTo(x0, ty); ctx.lineTo(x0 + largo, ty); }
      ctx.stroke();
    });
    ctx.restore();
  }
  ctx.strokeStyle = BORDE_MARCADO;
  ctx.lineWidth = 2;
  for (const { cel, x, y, w, h } of celdas) {
    const b = cel.estilo.bordes;
    if (!b.t && !b.r && !b.b && !b.l) continue;
    ctx.beginPath();
    if (b.t) { ctx.moveTo(x, y + 1); ctx.lineTo(x + w, y + 1); }
    if (b.b) { ctx.moveTo(x, y + h); ctx.lineTo(x + w, y + h); }
    if (b.l) { ctx.moveTo(x + 1, y); ctx.lineTo(x + 1, y + h); }
    if (b.r) { ctx.moveTo(x + w, y); ctx.lineTo(x + w, y + h); }
    ctx.stroke();
  }
  return lienzo;
}

/* ── Al portapapeles ─────────────────────────────────────────────────────── */

// El navegador de verdad, salvo que la prueba pase otro.
const NAV = () => (typeof navigator === "undefined" ? null : navigator);
const WIN = () => (typeof window === "undefined" ? null : window);

/* El «Copiar» de los menús: el texto y la tabla juntos. Sin la API nueva
   (`ClipboardItem`), si el navegador no deja o si no hay tabla (una celda
   sola), el texto solo, como antes. Devuelve si quedó algo copiado. (Ctrl+C
   no pasa por acá: lo pone el evento `copy`, que no pide permiso.) */
export async function copiarTextoYTabla({ texto, html }, { nav = NAV(), win = WIN() } = {}) {
  const c = nav && nav.clipboard;
  const Item = win && win.ClipboardItem;
  if (html && c && typeof c.write === "function" && typeof Item === "function") {
    try {
      await c.write([new Item({
        "text/plain": new Blob([texto], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      })]);
      return true;
    } catch { /* sigue con el texto solo */ }
  }
  if (c && typeof c.writeText === "function") {
    try { await c.writeText(texto); return true; } catch { return false; }
  }
  return false;
}

/* La imagen del lienzo al portapapeles como `image/png`. Se llama en el clic,
   sin esperar a que el PNG esté hecho: `ClipboardItem` recibe la promesa
   (Safari lo exige así; Chrome también lo acepta). Si un navegador no acepta
   la promesa, se prueba otra vez con el PNG ya hecho. Devuelve
   `{ copiada, png }`: `png` (la promesa del Blob) sirve para ofrecer bajarla
   cuando no se pudo copiar. */
export function copiarImagen(lienzo, { nav = NAV(), win = WIN() } = {}) {
  const png = new Promise((ok, mal) => {
    try {
      lienzo.toBlob((b) => (b ? ok(b) : mal(new Error("sin imagen"))), "image/png");
    } catch (e) { mal(e); }
  });
  png.catch(() => {}); // lo atiende quien la usa; que no quede como error suelto
  const no = () => ({ copiada: false, png });
  const c = nav && nav.clipboard;
  const Item = win && win.ClipboardItem;
  if (!c || typeof c.write !== "function" || typeof Item !== "function") return Promise.resolve(no());
  let item;
  try { item = new Item({ "image/png": png }); } catch { return Promise.resolve(no()); }
  return c.write([item]).then(
    () => ({ copiada: true, png }),
    () => png.then((b) => c.write([new Item({ "image/png": b })])).then(() => ({ copiada: true, png }), no),
  );
}

/* El nombre del archivo para bajar la imagen: la planilla, la hoja y el
   rango, sin lo que Windows no acepta en un nombre. */
export function nombreDeImagen(planilla, hoja, rg) {
  const limpio = (s) => String(s || "").replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();
  const desde = refDe(rg.r0, rg.c0);
  const rango = rg.r0 === rg.r1 && rg.c0 === rg.c1 ? desde : `${desde}-${refDe(rg.r1, rg.c1)}`;
  return `${[limpio(planilla), limpio(hoja)].filter(Boolean).join(" - ")} ${rango}.png`;
}
